/** 狀態與類型的中文標籤、徽章配色。
 *
 *  全站共用，不要各頁自己寫一份 —— 原生版就是靠這張表，
 *  React 版一度漏掉導致畫面直接顯示 person_detected 這種原始值。
 */
import type { BadgeProps } from '@/components/ui/badge';

type Variant = NonNullable<BadgeProps['variant']>;

/* ── 事件類型 ─────────────────────────────────────── */
export const EVENT_LABEL: Record<string, string> = {
  person_detected: '偵測到人',
  sound_anomaly: '異常聲響',
  motion: '移動偵測',
  tamper: '裝置遭破壞',
  recording: '錄影片段',
};
/** 未知類型直接顯示原字串，不要吞掉新型別。 */
export const evName = (t?: string | null) => (t ? EVENT_LABEL[t] ?? t : '—');

/* ── 判讀狀態 ─────────────────────────────────────── */
export const REVIEW_OPTIONS = [
  { key: 'unreviewed', label: '待確認' },
  { key: 'confirmed', label: '已確認' },
  { key: 'false_positive', label: '誤報' },
  { key: 'uncertain', label: '不確定' },
] as const;
const REVIEW_LABEL: Record<string, string> = {
  unreviewed: '待確認', confirmed: '已確認', false_positive: '誤報', uncertain: '不確定',
};
const REVIEW_VARIANT: Record<string, Variant> = {
  unreviewed: 'warn', confirmed: 'success', false_positive: 'danger', uncertain: 'muted',
};
export const reviewName = (s?: string | null) => (s ? REVIEW_LABEL[s] ?? s : '—');
export const reviewVariant = (s?: string | null): Variant =>
  (s && REVIEW_VARIANT[s]) || 'muted';

/* ── 檔案狀態 ─────────────────────────────────────── */
const MEDIA_LABEL: Record<string, string> = {
  expected: '待上傳', pending: '待上傳', uploading: '上傳中',
  uploaded: '已接收', verifying: '驗證中', stored: '已保存', failed: '失敗',
  cancelling: '取消中', cancelled: '已取消', deleting: '刪除中',
  delete_failed: '刪除失敗', deleted: '已刪除',
};
const MEDIA_VARIANT: Record<string, Variant> = {
  expected: 'muted', pending: 'muted', uploading: 'info',
  uploaded: 'info', verifying: 'info', stored: 'success', failed: 'danger',
  cancelling: 'warn', cancelled: 'muted', deleting: 'warn',
  delete_failed: 'danger', deleted: 'muted',
};
export const mediaName = (s?: string | null) => (s ? MEDIA_LABEL[s] ?? s : '—');
export const mediaVariant = (s?: string | null): Variant =>
  (s && MEDIA_VARIANT[s]) || 'muted';

/* ── 命令狀態 ─────────────────────────────────────── */
const CMD_LABEL: Record<string, string> = {
  created: '已建立', sent: '已下發', succeeded: '成功',
  failed: '失敗', timed_out: '逾時', running: '執行中',
};
const CMD_VARIANT: Record<string, Variant> = {
  created: 'muted', sent: 'info', succeeded: 'success',
  failed: 'danger', timed_out: 'warn', running: 'info',
};
export const cmdName = (s?: string | null) => (s ? CMD_LABEL[s] ?? s : '—');
export const cmdVariant = (s?: string | null): Variant =>
  (s && CMD_VARIANT[s]) || 'muted';

/* ── 裝置種類 ─────────────────────────────────────── */
const KIND_LABEL: Record<string, string> = {
  camera_audio: '影像＋聲音', camera: '僅影像', audio: '僅聲音',
};
export const kindName = (k?: string | null) => (k ? KIND_LABEL[k] ?? k : '—');
